import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

export interface CategoryDB {
  id: string;
  name: string;
  description: string;
  icon: string;
  visible: boolean;
  sort_order: number;
  image: string;
  providerCount: number;
  courseCount: number;
  isNew: boolean;
}

const CATEGORY_IMAGES: Record<string, string> = {
  "しみ抜き・衣類ケア": "https://public.readdy.ai/ai/img_res/c6f0d99ac425eae7d32ca3befade726b.jpg",
  "和服・着物ケア": "https://readdy.ai/api/search-image?query=Beautiful%20Japanese%20kimono%20silk%20fabric%20neatly%20folded%20traditional%20textile%20wooden%20lacquer%20box%20elegant%20studio%20minimal%20background%20no%20people&width=400&height=260&seq=cat-kimono-v2&orientation=landscape",
  "大工・木工": "https://readdy.ai/api/search-image?query=Japanese%20traditional%20carpentry%20woodworking%20tools%20chisels%20planes%20handsaw%20beautifully%20arranged%20dark%20oak%20workshop%20bench%20precision%20crafts%20no%20people&width=400&height=260&seq=cat-wood-v2&orientation=landscape",
  "料理・発酵食品": "https://readdy.ai/api/search-image?query=Japanese%20traditional%20fermented%20food%20miso%20wooden%20barrels%20sake%20vessels%20rustic%20artisan%20kitchen%20ingredients%20warm%20dark%20atmosphere%20elegant%20no%20people&width=400&height=260&seq=cat-food-v2&orientation=landscape",
  "陶芸・工芸": "https://readdy.ai/api/search-image?query=Japanese%20pottery%20ceramics%20clay%20bowls%20vases%20kiln%20studio%20elegant%20natural%20light%20craft%20workspace%20minimal%20dark%20background%20no%20people&width=400&height=260&seq=cat-pottery-v2&orientation=landscape",
  "理容・美容技術": "https://readdy.ai/api/search-image?query=Japanese%20barber%20salon%20professional%20scissors%20comb%20razor%20traditional%20grooming%20tools%20elegant%20dark%20studio%20minimal%20arrangement%20close%20up%20no%20people&width=400&height=260&seq=cat-beauty-v2&orientation=landscape",
  "和菓子・製菓": "https://readdy.ai/api/search-image?query=Japanese%20wagashi%20confectionery%20seasonal%20traditional%20sweets%20beautiful%20arrangement%20elegant%20dark%20lacquer%20plate%20minimal%20background%20no%20people&width=400&height=260&seq=cat-wagashi-v2&orientation=landscape",
  "書道・アート": "https://readdy.ai/api/search-image?query=Japanese%20calligraphy%20brush%20ink%20stone%20traditional%20art%20tools%20elegant%20minimal%20workspace%20black%20ink%20on%20rice%20paper%20dark%20background%20no%20people&width=400&height=260&seq=cat-calligraphy-v2&orientation=landscape",
};

const DEFAULT_IMAGE = "https://readdy.ai/api/search-image?query=Japanese%20traditional%20craft%20tools%20elegant%20minimal%20dark%20studio%20atmosphere%20objects%20no%20people%20professional%20artisan%20equipment%20wooden%20background&width=400&height=260&seq=cat-default-v2&orientation=landscape";

export function useCategories() {
  const [categories, setCategories] = useState<CategoryDB[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("categories")
      .select("id, name, description, icon, visible, sort_order")
      .eq("visible", true)
      .order("sort_order", { ascending: true });

    if (data) {
      setCategories(
        data.map((c, i) => ({
          ...c,
          image: CATEGORY_IMAGES[c.name] || DEFAULT_IMAGE,
          providerCount: 0,
          courseCount: 0,
          isNew: i >= 6,
        }))
      );
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return { categories, loading, refetch: load };
}

export async function fetchCategoryById(id: string): Promise<CategoryDB | null> {
  const { data } = await supabase
    .from("categories")
    .select("id, name, description, icon, visible, sort_order")
    .eq("id", id)
    .maybeSingle();
  if (!data) return null;
  return {
    ...data,
    image: CATEGORY_IMAGES[data.name] || DEFAULT_IMAGE,
    providerCount: 0,
    courseCount: 0,
    isNew: false,
  };
}
